import { useState, useEffect, useMemo } from 'react';
import * as echarts from 'echarts';
import { notification } from 'tea-component';
import { getOverviewHeadcount, getStudioHeadcount } from '@sg/services/index';
import { chartConfig } from '@sg/utils/chatUtils';
import {
  filterValidAxisRows,
  normalizeMetricAxisFields,
  type TMetricScope,
} from '../../utils/normalizeMetricAxisRows';
import {
  resolveActualForecastStyle,
  resolveMetricTooltipDate,
} from '../../utils/metricChartTooltip';
import {
  buildHeadcountChartTableRows,
  getHeadcountChartSeriesConfig,
  getHeadcountStackSeriesConfig,
  getHeadcountEmploymentStackName,
  hasHeadcountLiveopsData,
  FTE_LEAF_KEYS,
  TEMPORARY_LEAF_KEYS,
  type THeadcountCategoryLevel,
} from '../config/headcountCategoryConfig';

const buildHeadcountBarColor = (
  seriesMeta: {
    color: string;
    barGradient?: { top: string; bottom: string };
  },
) => {
  if (seriesMeta.barGradient) {
    return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      { offset: 0, color: seriesMeta.barGradient.top },
      { offset: 1, color: seriesMeta.barGradient.bottom },
    ]);
  }
  return seriesMeta.color;
};

export type THeadRateScopeOptions =
  | { scope: 'studio'; studioId: string }
  | { scope: 'portfolio' };

export const useHeadRateData = (
  scopeOptions: THeadRateScopeOptions,
  activeDateTypeSegment: string,
  showHeadRateChatsType: any,
  selectedMonth?: string | null,
  headcountCategoryLevel: THeadcountCategoryLevel = 'functions',
  formatMessage?: (descriptor: { id: string }) => string,
) => {
  const [headcountRawData, setHeadcountRawData] = useState<any[]>([]);
  const [headcountUpdateTime, setHeadcountUpdateTime] = useState<string>('');
  const [headRateLoading, setHeadRateLoading] = useState<boolean>(false);
  const showFinancialsList = ['bar', 'table'];

  const scope: TMetricScope = scopeOptions.scope;
  const studioId = scopeOptions.scope === 'studio' ? scopeOptions.studioId : '';

  useEffect(() => {
    if (scope === 'studio' && !studioId) {
      setHeadcountRawData([]);
      setHeadcountUpdateTime('');
      setHeadRateLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setHeadRateLoading(true);
        // Clear before fetch so a null datatime from the new segment does not keep the old value.
        setHeadcountUpdateTime('');
        const yearMode = activeDateTypeSegment === 'year';
        const basePayload: {
          view_mode: 'by_month' | 'by_year';
          datatime?: string;
        } = {
          view_mode: yearMode ? 'by_year' : 'by_month',
        };
        if (selectedMonth) {
          basePayload.datatime = selectedMonth;
        }

        const res: any = scope === 'portfolio'
          ? await getOverviewHeadcount(basePayload)
          : await getStudioHeadcount({
            ...basePayload,
            studio_id: studioId,
          });
        const responseDatatime = res?.data?.datatime ?? '';
        setHeadcountUpdateTime(responseDatatime);

        const normalizedRows = filterValidAxisRows(
          (res?.data?.data || []).map((item: any) => {
            const axis = normalizeMetricAxisFields(item, scope);
            return {
              ...item,
              ...axis,
              // Tooltip date; empty row date falls back to response datatime.
              date: resolveMetricTooltipDate(item?.date, item?.month, responseDatatime),
            };
          }),
        );
        setHeadcountRawData(
          scope === 'studio'
            ? [...normalizedRows].sort((a: any, b: any) => a.key.localeCompare(b.key))
            : normalizedRows,
        );
      } catch (error) {
        console.error('Error fetching studio headcount data:', error);
        notification.error({
          description: 'Failed to fetch studio headcount data',
        });
        setHeadcountUpdateTime('');
      } finally {
        setHeadRateLoading(false);
      }
    };

    fetchData();
  }, [scope, studioId, activeDateTypeSegment, selectedMonth]);

  const headRateTableData = useMemo(
    () => buildHeadcountChartTableRows(headcountRawData, headcountCategoryLevel, activeDateTypeSegment),
    [headcountRawData, headcountCategoryLevel, activeDateTypeSegment],
  );

  const headcountChartTableData = useMemo(
    () => buildHeadcountChartTableRows(headcountRawData, headcountCategoryLevel, activeDateTypeSegment),
    [headcountRawData, headcountCategoryLevel, activeDateTypeSegment],
  );

  // Category selector (Level 1/2/Functions) is only meaningful when at least
  // one leaf metric has a real value; otherwise every level renders "No data".
  const hasHeadcountData = useMemo(() => {
    const allLeafKeys: readonly string[] = [...FTE_LEAF_KEYS, ...TEMPORARY_LEAF_KEYS];
    return headcountRawData.some((row: any) => (
      allLeafKeys.some((key) => row?.[key] !== null && row?.[key] !== undefined)
    ));
  }, [headcountRawData]);

  // 数据集中任一行存在 l2_liveops 时，Level 2 图表/宽表/tooltip 均需展示 LiveOps
  const hasLiveopsData = useMemo(
    () => hasHeadcountLiveopsData(headcountRawData),
    [headcountRawData],
  );

  const headRateData = useMemo(() => {
    const seriesConfig = getHeadcountChartSeriesConfig(
      headcountCategoryLevel,
      undefined,
      undefined,
      hasLiveopsData,
    );
    const stackSeriesConfig = getHeadcountStackSeriesConfig(
      headcountCategoryLevel,
      hasLiveopsData,
    );
    const chartSourceData = headcountChartTableData;
    // Portfolio x-axis is studio label; Studios remains date.
    const axisKey = scope === 'portfolio' ? 'label' : 'date';

    return chartConfig(chartSourceData, showFinancialsList, (data: any) => {
      const datas = data;
      const orderedSeriesKeys = stackSeriesConfig.map((series) => series.chartKey);

      return orderedSeriesKeys
        .filter((chartKey) => Array.isArray(datas[chartKey]))
        .map((chartKey) => {
          const seriesMeta = seriesConfig.find(
            (series) => series.chartKey === chartKey,
          );
          if (!seriesMeta) {
            return null;
          }

          const seriesName = formatMessage
            ? formatMessage({ id: seriesMeta.labelKey })
            : seriesMeta.chartKey;

          return {
            name: seriesName,
            barGap: '0%',
            tooltipWidth: '320px',
            color: seriesMeta.color,
            // Portfolio has ~20 studios + FTE/Temporary pair; keep bars thinner than Studios.
            barWidth: scope === 'portfolio' ? '24%' : '40%',
            // White border separates adjacent stacked segments visually.
            itemStyle: {
              color: buildHeadcountBarColor(seriesMeta),
              borderColor: '#fff',
              borderWidth: 0.5,
            },
            data: datas[chartKey].map((items: any, index: number) => {
              const axisRow = chartSourceData[index] || {};
              // Null source defaults to Actual; only source === 'forecast' dims.
              const { useForecastStyle, tooltipHeaderRight } = resolveActualForecastStyle({
                source: axisRow.source as string | null | undefined,
              });
              return {
                value: items,
                tooltipWidth: '320px',
                valueType: 'integer',
                employmentType: seriesMeta.employmentType,
                chartKey: seriesMeta.chartKey,
                // Solid marker for tooltip; bar fill may use barGradient separately.
                markerColor: seriesMeta.color,
                // Optional tooltip title (Portfolio maps studio_name → title).
                title: axisRow.title ?? axisRow.studio_name,
                date: axisRow.date,
                isActual: !useForecastStyle,
                tooltipHeaderRight,
                itemStyle: {
                  color: buildHeadcountBarColor(seriesMeta),
                  borderColor: '#fff',
                  borderWidth: 0.5,
                  opacity: useForecastStyle ? 0.3 : 1,
                },
              };
            }),
            type: showHeadRateChatsType?.value,
            stack: getHeadcountEmploymentStackName(seriesMeta.employmentType),
            showSymbol: showHeadRateChatsType?.format,
          };
        })
        .filter(Boolean);
    }, '', axisKey);
  }, [
    headcountChartTableData,
    headcountCategoryLevel,
    showHeadRateChatsType,
    formatMessage,
    scope,
    hasLiveopsData,
  ]);

  return {
    headRateData,
    headRateTableData,
    headcountUpdateTime,
    headRateLoading,
    hasHeadcountData,
    hasLiveopsData,
  };
};
